import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BookOpen, Calculator, FileInput, FileOutput, CheckCircle2, ShieldCheck, Clock, DollarSign } from "lucide-react";
import { blogPosts } from "@/data/blogPosts";

const services = [
  { icon: BookOpen, title: "Bookkeeping", desc: "Daily transaction recording, bank reconciliation, and clean monthly financials." },
  { icon: Calculator, title: "Accounting", desc: "Month-end close, financial statements, and reporting your CPA will love." },
  { icon: FileInput, title: "Accounts Payable", desc: "Invoice processing, approvals, and on-time vendor payments — every time." },
  { icon: FileOutput, title: "Accounts Receivable", desc: "Faster invoicing, payment tracking, and collections that protect cash flow." },
];

const stats = [
  { value: "$9.99", label: "Per hour, starting price" },
  { value: "500+", label: "Businesses served" },
  { value: "99.8%", label: "Accuracy rate" },
  { value: "24hr", label: "Response time" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2">
          <div className="py-16 lg:py-28 lg:pr-16 fade-up">
            <span className="inline-block bg-[#FFF3ED] text-[#E04F00] text-xs font-bold uppercase tracking-widest px-3 py-1.5 mb-6 border border-[#FF5A00]/20">
              USA &amp; Canada · 1 Week Free Trial
            </span>
            <h1 className="font-heading font-extrabold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl">
              Bookkeeping that costs <span className="text-[#FF5A00]">less</span>. Books that are <span className="text-[#FF5A00]">sure</span>.
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
              Core Sure Business Services delivers expert bookkeeping, accounting, accounts payable and accounts receivable for US &amp; Canadian businesses — starting at just <span className="font-mono-price font-bold text-foreground">$9.99/hour</span>.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/appointment"
                data-testid="hero-cta-trial"
                className="inline-flex items-center justify-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold px-8 py-4 transition-colors"
              >
                Start 1 Week Free Trial <ArrowRight size={18} />
              </Link>
              <Link
                to="/pricing"
                data-testid="hero-cta-pricing"
                className="inline-flex items-center justify-center gap-2 border border-foreground font-bold px-8 py-4 hover:bg-foreground hover:text-white transition-colors"
              >
                View Pricing
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
              {["No long-term contracts", "QuickBooks & Xero certified", "Cancel anytime"].map((t) => (
                <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={15} className="text-[#FF5A00]" /> {t}</span>
              ))}
            </div>
          </div>
          <div className="relative lg:border-l border-border hidden lg:block">
            <img
              src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=1000&q=80"
              alt="Team collaborating on bookkeeping"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-8 left-8 bg-white border border-border p-5 max-w-xs">
              <p className="font-mono-price text-2xl font-bold text-[#FF5A00]">$9.99<span className="text-sm text-muted-foreground">/hr</span></p>
              <p className="text-sm text-muted-foreground mt-1">Professional bookkeeping tasks. Try free for 1 week.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-border bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4">
          {stats.map((s, i) => (
            <div key={s.label} className={`px-6 py-10 ${i !== 0 ? "border-l border-[#27272A]" : ""}`}>
              <p className="font-mono-price text-3xl lg:text-4xl font-bold text-[#FF5A00]">{s.value}</p>
              <p className="text-sm text-zinc-400 mt-2">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services grid */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#FF5A00]">What we do</span>
              <h2 className="font-heading font-extrabold tracking-tight text-3xl md:text-4xl mt-2">Four services. One reliable team.</h2>
            </div>
            <Link to="/services" data-testid="home-services-link" className="inline-flex items-center gap-1 font-bold text-sm hover:text-[#FF5A00] transition-colors">
              All services <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border border-border">
            {services.map(({ icon: Icon, title, desc }, i) => (
              <div
                key={title}
                data-testid={`home-service-card-${i}`}
                className={`p-8 hover-lift bg-white hover:bg-[#FFF3ED] ${i !== 0 ? "border-t md:border-t-0 md:border-l border-border" : ""} ${i === 2 ? "md:border-t lg:border-t-0" : ""} ${i === 3 ? "md:border-t lg:border-t-0" : ""}`}
              >
                <Icon size={28} className="text-[#FF5A00]" />
                <h3 className="font-heading font-bold text-lg mt-4">{title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="border-b border-border bg-muted/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF5A00]">Why Core Sure</span>
            <h2 className="font-heading font-extrabold tracking-tight text-3xl md:text-4xl mt-2">Built for small business budgets</h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">Get in-house quality without in-house costs. Every engagement starts with a free week — so you can see the quality before you pay a cent.</p>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: DollarSign, title: "Transparent pricing", desc: "From $9.99/hr to full-time dedicated staff at $2,199/mo. No hidden fees, ever." },
              { icon: ShieldCheck, title: "Secure & compliant", desc: "Bank-level data security and GAAP-aligned processes for US & Canadian standards." },
              { icon: Clock, title: "Always on time", desc: "Books closed by the 5th business day. Payables paid on schedule. Guaranteed." },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white border border-border p-6 hover-lift">
                <Icon size={24} className="text-[#FF5A00]" />
                <h3 className="font-heading font-bold mt-3">{title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog teaser */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <h2 className="font-heading font-extrabold tracking-tight text-3xl md:text-4xl">From the blog</h2>
            <Link to="/blog" data-testid="home-blog-link" className="inline-flex items-center gap-1 font-bold text-sm hover:text-[#FF5A00] transition-colors">
              All articles <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogPosts.slice(0, 3).map((post) => (
              <Link key={post.slug} to={`/blog/${post.slug}`} data-testid={`home-blog-card-${post.slug}`} className="group border border-border bg-white hover-lift block">
                <img src={post.image} alt={post.title} className="w-full h-44 object-cover" />
                <div className="p-6">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#FF5A00]">{post.category}</span>
                  <h3 className="font-heading font-bold text-lg mt-2 leading-snug group-hover:text-[#FF5A00] transition-colors">{post.title}</h3>
                  <p className="text-xs text-muted-foreground mt-3">{post.date} · {post.readTime}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="font-heading font-extrabold tracking-tight text-3xl md:text-5xl">Try us free for 1 week.<br />Keep us if you love us.</h2>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto">No credit card required to start. Book a free consultation and get your first week of bookkeeping on us.</p>
          <Link
            to="/appointment"
            data-testid="home-bottom-cta"
            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold px-10 py-4 mt-8 transition-colors"
          >
            Book Your Free Consultation <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
